import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function PlatformRequirements() {
  return (
    <>
      <Helmet>
        <title>Platform Requirements - Business Verification | RMQ Digital</title>
        <meta name="description" content="Requirements for businesses using RMQ Digital platform. Opt-in documentation, compliance verification, and account approval process." />
      </Helmet>

      <div style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)', color: 'white', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}>Platform Requirements</h1>
          <p style={{ fontSize: '1.25rem', opacity: 0.95 }}>What businesses need to use our platform</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        {/* Requirements Hero Image */}
        <div style={{ marginBottom: '4rem', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <img 
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=400&fit=crop" 
            alt="Business verification and compliance" 
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
          />
        </div>

        <div style={{ background: '#ede9fe', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', border: '2px solid #8b5cf6' }}>
          <h2 style={{ color: '#6d28d9', marginBottom: '1rem', fontSize: '1.5rem' }}>Who Can Use RMQ Digital?</h2>
          <p style={{ color: '#5b21b6', fontSize: '1.1rem', lineHeight: '1.8' }}>
            RMQ Digital is a B2B platform for <strong>verified businesses only</strong>. Each business must complete our verification process 
            and demonstrate they have proper opt-in consent from their customers before sending any messages.
          </p>
        </div>

        <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem' }}>
          Business Verification Requirements
        </h2>

        <div style={{ display: 'grid', gap: '2rem', marginBottom: '3rem' }}>
          <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6d28d9', marginBottom: '1rem' }}>
              1. Business Documentation
            </h3>
            <p style={{ color: '#4b5563', marginBottom: '1rem' }}>All businesses must provide:</p>
            <ul style={{ color: '#6b7280', lineHeight: '2', paddingLeft: '2rem' }}>
              <li>Business name and EIN (Employer Identification Number)</li>
              <li>Business address and contact information</li>
              <li>Business website or online presence</li>
              <li>Business registration documents (if requested)</li>
              <li>Owner/authorized representative identification</li>
            </ul>
          </div>

          <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6d28d9', marginBottom: '1rem' }}>
              2. Opt-In Documentation
            </h3>
            <p style={{ color: '#4b5563', marginBottom: '1rem' }}>Businesses must demonstrate how they collect customer consent:</p>
            <ul style={{ color: '#6b7280', lineHeight: '2', paddingLeft: '2rem' }}>
              <li>Screenshots of opt-in forms or signup pages</li>
              <li>Description of opt-in process (website, app, in-person, etc.)</li>
              <li>Sample consent language shown to customers</li>
              <li>Proof that customers knowingly provided their contact information</li>
              <li>Documentation of opt-out handling procedures</li>
            </ul>
          </div>

          <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6d28d9', marginBottom: '1rem' }}>
              3. Use Case Description
            </h3>
            <p style={{ color: '#4b5563', marginBottom: '1rem' }}>Businesses must clearly describe their messaging use case:</p>
            <ul style={{ color: '#6b7280', lineHeight: '2', paddingLeft: '2rem' }}>
              <li>What types of messages will be sent (appointments, orders, alerts, etc.)</li>
              <li>Who will receive messages (existing customers only)</li>
              <li>How often messages will be sent (frequency)</li>
              <li>Sample message content</li>
              <li>Business relationship with recipients</li>
            </ul>
          </div>
        </div>

        <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem' }}>
          Compliance Requirements
        </h2>

        <div style={{ background: '#f9fafb', padding: '2rem', borderRadius: '12px', marginBottom: '3rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            All businesses must agree to:
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {[
              'Only message customers who have explicitly opted in',
              'Maintain records of all opt-in consent',
              'Honor STOP/UNSUBSCRIBE requests immediately',
              'Follow TCPA, CTIA, and carrier regulations',
              'Not send unsolicited promotional messages',
              'Not use purchased or scraped contact lists',
              'Not send high-risk or prohibited content',
              'Allow platform compliance monitoring',
              'Accept account suspension for violations',
              'Provide additional documentation if requested'
            ].map((req, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ color: '#6d28d9', fontWeight: '600', marginTop: '0.25rem' }}>✓</span>
                <span style={{ color: '#4b5563', lineHeight: '1.6' }}>{req}</span>
              </div>
            ))}
          </div>
        </div>

        <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem' }}>
          Approval Process
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
          <div style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📝</div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>Step 1: Application</h4>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Submit business information and documentation</p>
          </div>

          <div style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔍</div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>Step 2: Review</h4>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>We verify business legitimacy and compliance</p>
          </div>

          <div style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>Step 3: Approval</h4>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Account activated with messaging capabilities</p>
          </div>

          <div style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📊</div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>Step 4: Monitoring</h4>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Ongoing compliance monitoring and support</p>
          </div>
        </div>

        <div style={{ background: '#fee2e2', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', border: '2px solid #ef4444' }}>
          <h3 style={{ color: '#991b1b', marginBottom: '1rem', fontSize: '1.5rem' }}>Account Suspension Policy</h3>
          <p style={{ color: '#7f1d1d', marginBottom: '1rem', lineHeight: '1.8' }}>
            We reserve the right to immediately suspend accounts that:
          </p>
          <ul style={{ color: '#7f1d1d', lineHeight: '2', paddingLeft: '2rem' }}>
            <li>Send messages without proper opt-in consent</li>
            <li>Use purchased or scraped contact lists</li>
            <li>Fail to honor STOP/UNSUBSCRIBE requests</li>
            <li>Send prohibited or high-risk content</li>
            <li>Violate TCPA, CTIA, or carrier policies</li>
            <li>Receive excessive spam complaints</li>
            <li>Provide false information during verification</li>
          </ul>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)', color: 'white', padding: '3rem', borderRadius: '12px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
            Ready to Apply?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.95 }}>
            If your business meets these requirements, apply for platform access
          </p>
          <a href="/contact" style={{
            display: 'inline-block',
            background: 'white',
            color: '#6d28d9',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem'
          }}>
            Apply Now
          </a>
        </div>
      </div>
    </>
  );
}
